import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OnboardingImportItem } from '../../api/account/accountApiTypes';
import {
    actionKey,
    changeLines,
    issueText,
    itemLabel,
    itemRowKey,
    partDetailLine,
    priceChangeLines,
    windowChangeLines,
} from './importReportView';

const card = 'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 flex flex-col gap-3';
const headerCell = 'text-left py-2 pr-3 text-foreground/60 font-medium';

/** A column a page adds to the preview: the onboarding import's attachments, the organisation import's type. */
export interface ImportItemsExtraColumn {
    header: string;
    render: (item: OnboardingImportItem) => React.ReactNode;
}

/**
 * One group of an import preview as a card: per item its name (with every
 * changed field, price row and fixed weekly time under it), what happens to
 * it, and its errors and hints. Shared by "Einrichtung per Datei" and
 * "Organisationen importieren" (plan docs/plans/organization-import.md §5.3).
 */
const ImportItemsTable: React.FC<{
    title: string;
    items: OnboardingImportItem[];
    extraColumn?: ImportItemsExtraColumn;
}> = ({ title, items, extraColumn }) => {
    const { t, i18n } = useTranslation();

    /** An update's changes under its name: field by field, each price row and each fixed time created, changed or removed. */
    const renderChanges = (item: OnboardingImportItem) => {
        const detail = partDetailLine(item, t);
        const fields = changeLines(item, t, i18n.language);
        const prices = priceChangeLines(item, t, i18n.language);
        const windows = windowChangeLines(item, t, i18n.language);
        if (!detail && fields.length === 0 && prices.length === 0 && windows.length === 0) return null;
        return (
            <ul className={'mt-1 flex flex-col gap-0.5 text-xs text-foreground/70'}>
                {detail && <li>{detail}</li>}
                {fields.map((line) => <li key={line}>{line}</li>)}
                {[...prices, ...windows].map((line) => (
                    <li key={line.text} className={line.kind === 'remove' ? 'font-medium text-foreground' : undefined}>{line.text}</li>
                ))}
            </ul>
        );
    };

    return (
        <section className={card}>
            <h2 className={'text-base font-semibold text-foreground'}>{title}</h2>
            <div className={'overflow-x-auto'}>
                <table className={'w-full text-sm'}>
                    <thead>
                        <tr className={'border-b border-foreground/10'}>
                            <th className={headerCell}>{t('onboarding_import.column.item')}</th>
                            <th className={headerCell}>{t('onboarding_import.column.action')}</th>
                            <th className={extraColumn ? headerCell : 'text-left py-2 text-foreground/60 font-medium'}>
                                {t('onboarding_import.column.notes')}
                            </th>
                            {extraColumn && <th className={'text-left py-2 text-foreground/60 font-medium'}>{extraColumn.header}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={itemRowKey(item)} className={'border-b border-foreground/5 align-top'}>
                                <td className={'py-2 pr-3 text-foreground'}>
                                    {itemLabel(item, t)}
                                    {renderChanges(item)}
                                </td>
                                <td className={item.skipped ? 'py-2 pr-3 font-medium text-foreground' : 'py-2 pr-3 text-foreground'}>
                                    {t(actionKey(item))}
                                </td>
                                <td className={extraColumn ? 'py-2 pr-3' : 'py-2'}>
                                    {item.issues.length === 0
                                        ? <span className={'text-foreground/40'}>{t('common.not_applicable')}</span>
                                        : (
                                            <ul className={'flex flex-col gap-1'}>
                                                {item.issues.map((issue) => (
                                                    <li
                                                        key={`${issue.code}-${issue.field ?? ''}`}
                                                        className={issue.level === 'error' ? 'font-medium text-foreground' : 'text-foreground/70'}
                                                    >
                                                        {issueText(issue, t, i18n.language)}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                </td>
                                {extraColumn && <td className={'py-2'}>{extraColumn.render(item)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default ImportItemsTable;
